import {
  DEFAULT_COUNTRY,
  DEFAULT_LANGUAGE,
  LOCALE_FORMAT,
  SUPPORTED_COUNTRIES,
} from '@src/config/localisation/locale-config.constants'
import type { TAbsoluteLocale, TCountry, TRegionDetail } from '@src/config/localisation/locale-config.types'
import { parseFormat } from '@src/utils/general.utils'
import { parseLocale } from '@src/utils/i18n.utils'

export const getFullLocaleForRegion = (target: TAbsoluteLocale | TCountry, regionDetails: TRegionDetail): string => {
  let targetLocale = target
  const region = (regionDetails.regions[targetLocale as TCountry]?.regionCode ??
    parseFormat(LOCALE_FORMAT, targetLocale as TAbsoluteLocale)?.country ??
    '') as TCountry

  //if the confirmation locale is the same as the region, set the confirmation locale to the default language iso code
  if (target === region) {
    targetLocale = regionDetails.regions[region]?.defaultLanguageIsoCode as TAbsoluteLocale
  }
  return targetLocale
}

export const checkRegionChanged = ({
  locale,
  regionDetails,
}: {
  locale: TAbsoluteLocale
  regionDetails: TRegionDetail
}): {
  isRegionChange: boolean
  targetLocale?: TAbsoluteLocale | TCountry | ''
} => {
  const { country: currentCountry } = parseLocale(locale)

  if (typeof window === 'undefined') {
    return { isRegionChange: false, targetLocale: '' }
  }

  try {
    // During client-side navigation, window.location might be temporarily invalid
    if (!window.location.origin || !window.location.pathname) {
      return { isRegionChange: false, targetLocale: '' }
    }
    const currentURL = new URL(window.location.pathname, window.location.origin)
    const pathnameParts = currentURL.pathname.split('/')
    const regionInfoFromURL = pathnameParts[1]?.toLowerCase()

    const isValidURLRegionInfo =
      regionDetails.regions[regionInfoFromURL as TCountry] ??
      Object.values(regionDetails.regions).some(region =>
        Object.hasOwn(region.supportedLanguages, regionInfoFromURL as TAbsoluteLocale),
      )
    //target region info can either be a country or a language, targetLocale is the full locale with the language and country, default if regionInfoFromURL is only country
    const targetRegionInfo = isValidURLRegionInfo && regionInfoFromURL ? regionInfoFromURL : ''
    const targetLocale = getFullLocaleForRegion(targetRegionInfo as TAbsoluteLocale | TCountry, regionDetails)

    if (targetLocale) {
      const isRegionChange = parseLocale(targetLocale as TAbsoluteLocale).country !== currentCountry
      return {
        isRegionChange,
        targetLocale: targetLocale as TAbsoluteLocale,
      }
    }
    return {
      isRegionChange: false,
      targetLocale: targetLocale as TAbsoluteLocale,
    }
  } catch (error) {
    // If URL construction fails during navigation, return safe defaults
    return { isRegionChange: false, targetLocale: '' }
  }
}

export const getUpdatedLocaleForURLAfterRegionSwitch = (
  targetLocale: TAbsoluteLocale | TCountry,
  regionDetails: TRegionDetail,
): string => {
  // Handle default locale - no prefix needed
  if (targetLocale === DEFAULT_COUNTRY || targetLocale === DEFAULT_LANGUAGE) {
    return ''
  }

  let localeInfo
  //Get locale info from region details
  if (SUPPORTED_COUNTRIES.includes(targetLocale as TCountry)) {
    localeInfo = regionDetails.regions[targetLocale as TCountry]
  } else if (targetLocale.length > 0) {
    localeInfo = regionDetails.regions[parseFormat(LOCALE_FORMAT, targetLocale as TAbsoluteLocale)?.country as TCountry]
  }

  //if the locale is the same as the region or the default language iso code, return country
  if (targetLocale === localeInfo?.regionCode || targetLocale === localeInfo?.defaultLanguageIsoCode) {
    const country =
      targetLocale === localeInfo.regionCode ? targetLocale : parseLocale(targetLocale as TAbsoluteLocale).country
    return country
  } else {
    return targetLocale
  }
}
